/**
 * Copyright Mántum All Rights Reserved.
 */
import Label from '../label';
import store from '../internal/store';
import useSWR from 'swr';
import { ACTION_VALUE, useGroup } from '../../group';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { helper } from '@mantum/kanela';

import type { DataSource, Option, Options, Validation } from '@mantum/kanela';

export type PropTypes = {
    advanced?: {
        options?: Option<string>;
        orientation?: 'vertical' | 'horizontal';
    };
    dataSource?: DataSource | Options<string>;
    disabled?: boolean;
    error?: boolean;
    label?: string;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    testid?: string;
    validation?: Validation;
    value?: string;
};

export default forwardRef<HTMLDivElement, PropTypes>(
    function Component(props, ref) {
        // prettier-ignore
        const {
        advanced = {},
        dataSource = [],
        name,
        value = ''
    } = props

        // prettier-ignore
        const {
        orientation = 'vertical'
    } = advanced

        const groups = useGroup();
        const changeRef = useRef(props.onChange);

        const fetcher = () => store(dataSource);

        const { data } = useSWR(() => {
            return !Array.isArray(dataSource) ? dataSource.url : null;
        }, fetcher);

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        const [currentValue, setCurrentValue] = useState<string | undefined>(
            props.value
        );

        useEffect(() => {
            setCurrentValue(value);
        }, [value]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, value);
            }
        }, [name, value]);

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            const content = groups[name];
            if (content.action === ACTION_VALUE) {
                const value = `${content.value}`;
                setCurrentValue(value);

                if (changeRef.current) {
                    changeRef.current(name, value);
                }
            }
        }, [name, groups]);

        const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;

            setCurrentValue(target.value);
            if (props.onChange) {
                props.onChange(props.name, target.value);
            }
        };

        const style = orientation === 'horizontal' ? 'flex-row' : 'flex-col';

        const values = data ? data : [];
        const items = helper.select.prepare(
            !Array.isArray(dataSource) ? values : dataSource,
            props.advanced?.options
        );

        return (
            <div className='flex flex-col'>
                <Label
                    name={props.label}
                    optional={props.optional}
                    disabled={props.disabled}
                />
                <div
                    className={`flex flex-wrap gap-1 ${style}`}
                    data-testid={props.testid}
                    data-value={currentValue}
                    ref={ref}
                >
                    {items.map((it) => (
                        <label
                            key={`${it.value}`}
                            className='flex items-center gap-1'
                        >
                            <input
                                checked={`${currentValue}` === `${it.value}`}
                                className='h-4 w-4 accent-primary'
                                disabled={props.disabled}
                                name={props.name}
                                onBlur={props.onBlur}
                                onChange={onChange}
                                type='radio'
                                value={`${it.value}`}
                            />
                            {it.label}
                        </label>
                    ))}
                </div>
            </div>
        );
    }
);
